import get from 'lodash/get';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import type { GroupBase, StylesConfig } from 'react-select';

import { Box } from '@/components/Box';
import { InputErrorMessage } from '@/components/InputErrorMessage';
import type { SelectOption, SelectProps } from '@/components/Select';
import { Select } from '@/components/Select';
import { getPlaceholderSuffix } from '@/lib/forms/helpers';
import { generateDataTestId } from '@/lib/tests';
import { theme } from '@/theme';

export interface SelectFieldProps extends SelectProps {
  name: string;
  phonePrefixDisplayFormat?: 'short' | 'dialCode';
  showError?: boolean;
  triggerValidationFields?: string[];
}

const errorStyles: StylesConfig<
  SelectOption,
  false,
  GroupBase<SelectOption>
> = {
  control: (styles) => ({
    ...styles,
    borderBottom: `1px solid ${theme.colors.error}`,
  }),
};

export const RESET_OPTION: SelectOption = {
  label: 'Reset',
  value: '',
};

export const SelectField = ({
  name,
  showError = true,
  ...props
}: SelectFieldProps) => {
  const { control, formState, trigger } = useFormContext();
  const { formatMessage } = useIntl();
  const placeholder = getPlaceholderSuffix(
    props.placeholder,
    props.required,
    formatMessage,
  );
  const error = get(formState.errors, name) as any;

  const triggerValidation = (
    fields: SelectFieldProps['triggerValidationFields'],
  ) => {
    formState.isSubmitted && trigger(fields);
  };
  const valueErrorMessage = error?.value;
  const labelErrorMessage = error?.label;

  return (
    <Box
      data-test-id='SelectFieldWrapper'
      {...generateDataTestId('select', 'wrapper')}
    >
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => (
          <>
            <Select
              data-testid='SelectFieldValue'
              onChange={(val) => {
                onChange(val?.label === RESET_OPTION.label ? null : val);
                props.triggerValidationFields &&
                  triggerValidation(props.triggerValidationFields);
              }}
              value={value}
              {...props}
              name={name}
              {...(!!error && { styles: errorStyles })}
              phonePrefixDisplayFormat={props.phonePrefixDisplayFormat}
              placeholder={placeholder}
            />
          </>
        )}
      />
      {showError && (
        <InputErrorMessage
          data-test-id='SelectFieldError'
          error={valueErrorMessage || labelErrorMessage || error}
        />
      )}
    </Box>
  );
};

SelectField.displayName = 'SelectField';
